import api from './api';

export interface WorkflowStage {
  name: string;
  description: string;
  color: string;
  icon: string;
  order: number;
  skippable?: boolean;
}

export interface WorkflowTemplate {
  _id: string;
  name: string;
  description: string;
  type: 'sale' | 'rental' | 'auction';
  saleType?: string;
  stages: WorkflowStage[];
  isDefault: boolean;
  agencyId?: string;
}

// Get all workflow templates for an agency
export const getWorkflowTemplates = async (agencySlug: string): Promise<WorkflowTemplate[]> => {
  const res = await api.get<WorkflowTemplate[]>(`/agencies/${agencySlug}/workflow-templates`);
  return res.data;
};

// Get cached workflow stage combinations from agency database
export const getWorkflowStageCombinations = async (agencySlug: string): Promise<string[]> => {
  const res = await api.get<{ combinations: string[] }>(`/agencies/${agencySlug}/workflow-stage-combinations`);
  return res.data.combinations;
};

// Get workflows for a property
export const getWorkflows = async (propertyId: string, agencySlug?: string): Promise<any[]> => {
  // Get agency slug from URL if not provided
  if (!agencySlug) {
    const urlParts = window.location.pathname.split('/');
    agencySlug = urlParts[urlParts.indexOf('agencies') + 1];
    if (!agencySlug) {
      throw new Error('Could not determine agency slug from URL');
    }
  }
  
  const res = await api.get<any[]>(`/agencies/${agencySlug}/properties/${propertyId}/workflows`);
  return res.data;
};

export { getWorkflows as getPropertyWorkflows };

// Force update workflow stage combinations (admin function)
export const updateWorkflowStageCombinations = async (agencySlug: string): Promise<string[]> => {
  const res = await api.post<{ combinations: string[] }>(`/agencies/${agencySlug}/workflow-stage-combinations/update`);
  return res.data.combinations;
};

// Legacy function for backward compatibility (deprecated)
export const getAllWorkflowStages = async (agencySlug: string): Promise<Array<{ workflowName: string; stage: WorkflowStage }>> => {
  const templates = await getWorkflowTemplates(agencySlug);
  const stages: Array<{ workflowName: string; stage: WorkflowStage }> = [];
  
  templates.forEach(template => {
    template.stages.forEach(stage => {
      stages.push({
        workflowName: template.name,
        stage: stage
      });
    });
  });
  
  return stages;
};

// Detect and create missing workflows automatically
export const detectAndCreateMissingWorkflow = async (property: any, workflows: any[]): Promise<any[]> => {
  try {
    console.log('🔍 DETECTING MISSING WORKFLOWS');
    console.log('Property:', property._id, property.address?.street);
    console.log('Workflows count:', workflows.length);
    
    // If no workflows exist, this is a new property
    if (!workflows || workflows.length === 0) {
      console.log('No workflows found for property, this might be a new property');
      return workflows;
    }

    // Find the active workflow (the one that should be current)
    const activeWorkflow = workflows.find(w => w.isActive);
    console.log('Active workflow found:', activeWorkflow ? activeWorkflow.name : 'None');
    console.log('Active workflow status:', activeWorkflow ? activeWorkflow.status : 'None');
    console.log('Active workflow isActive:', activeWorkflow ? activeWorkflow.isActive : 'None');
    
    // If no active workflow, find the most recent completed workflow
    if (!activeWorkflow) {
      const completedWorkflows = workflows.filter(w => w.status === 'completed');
      console.log('Completed workflows count:', completedWorkflows.length);
      
      if (completedWorkflows.length === 0) {
        console.log('No completed workflows found');
        return workflows;
      }

      const lastCompletedWorkflow = completedWorkflows[completedWorkflows.length - 1];
      console.log('Last completed workflow:', lastCompletedWorkflow.name, 'type:', lastCompletedWorkflow.type);

      // Check if this completed workflow should have created a next workflow
      const shouldCreateNext = await shouldCreateNextWorkflow(lastCompletedWorkflow, property);
      console.log('Should create next workflow:', shouldCreateNext);
      
      if (shouldCreateNext) {
        console.log('🚀 Detected missing workflow, creating next workflow...');
        const newWorkflow = await createNextWorkflow(lastCompletedWorkflow, property);
        if (newWorkflow) {
          console.log('✅ New workflow created successfully:', newWorkflow.name);
          // Add the new workflow to the list and return updated list
          return [...workflows, newWorkflow];
        } else {
          console.log('❌ Failed to create new workflow');
        }
      } else {
        console.log('No next workflow needed for this completed workflow');
      }
    } else {
      console.log('Active workflow exists, checking if it should be completed...');
      
      // Check if the active workflow is actually completed and needs a next workflow
      if (activeWorkflow.status === 'completed') {
        console.log('Active workflow is completed, checking if it needs a next workflow...');
        const shouldCreateNext = await shouldCreateNextWorkflow(activeWorkflow, property);
        console.log('Should create next workflow from active completed workflow:', shouldCreateNext);
        
        if (shouldCreateNext) {
          console.log('🚀 Active workflow is completed and needs next workflow, creating...');
          const newWorkflow = await createNextWorkflow(activeWorkflow, property);
          if (newWorkflow) {
            console.log('✅ New workflow created successfully:', newWorkflow.name);
            // Add the new workflow to the list and return updated list
            return [...workflows, newWorkflow];
          } else {
            console.log('❌ Failed to create new workflow');
          }
        }
      } else {
        console.log('Active workflow is not completed, no missing workflow detection needed');
      }
    }

    return workflows;
  } catch (error) {
    console.error('❌ Error detecting missing workflow:', error);
    return workflows;
  }
};

// Check if a completed workflow should have created a next workflow
export const shouldCreateNextWorkflow = async (workflow: any, property: any): Promise<boolean> => {
  try {
    console.log('🔍 Checking if workflow should create next:', workflow.name, workflow.type);
    
    // Consultation workflow should create sale/auction workflow
    if (workflow.type === 'consultation') {
      console.log('Checking consultation workflow tasks...');
      
      const nextStepTask = workflow.stages
        .flatMap((s: any) => s.tasks)
        .find((t: any) => (t.taskType === 'next_steps_determination' || t.name === 'Determine Next Steps' || t.name === 'Agree Next Steps') && t.status === 'completed');
      
      const saleTypeTask = workflow.stages
        .flatMap((s: any) => s.tasks)
        .find((t: any) => (t.taskType === 'sale_type_determination' || t.name === 'Determine Sale Type') && t.status === 'completed');

      const nextStep = nextStepTask?.fieldValues?.nextStep;
      let saleType = saleTypeTask?.fieldValues?.saleType;
      const hasSaleTypeTask = workflow.stages.some((s: any) =>
        s.tasks.some((t: any) => t.taskType === 'sale_type_determination' || t.name === 'Determine Sale Type')
      );
      if (nextStep === 'Proceed' && !saleType && !hasSaleTypeTask) {
        saleType = 'rental';
      }

      console.log('Next Step task found:', !!nextStepTask);
      console.log('Sale Type task found:', !!saleTypeTask);
      console.log('Next Step value:', nextStep);
      console.log('Sale Type value:', saleType);
      console.log('Consultation workflow check - Next step:', nextStep, 'Sale type:', saleType);
      
      const shouldCreate = nextStep === 'Proceed' && saleType;
      console.log('Should create next workflow from consultation:', shouldCreate);
      return shouldCreate;
    }

    // Auction workflow should create sale workflow if unsuccessful
    if (workflow.type === 'auction' && workflow.name.includes('Auction') && !workflow.name.includes('Pre-auction')) {
      console.log('Checking auction workflow...');
      const auctionResultsTask = workflow.stages
        .flatMap((s: any) => s.tasks)
        .find((t: any) => t.name === 'Record Results' && t.status === 'completed');

      const auctionOutcome = auctionResultsTask?.fieldValues?.auctionOutcome;
      console.log('Auction workflow check - Auction outcome:', auctionOutcome);
      
      const shouldCreate = auctionOutcome === 'unsuccessful' || auctionOutcome === 'passed_in';
      console.log('Should create next workflow from auction:', shouldCreate);
      return shouldCreate;
    }

    // Pre-auction workflow should create auction workflow if unsuccessful
    if (workflow.type === 'auction' && workflow.name.includes('Pre-auction')) {
      console.log('Checking pre-auction workflow...');
      const preAuctionResultsTask = workflow.stages
        .flatMap((s: any) => s.tasks)
        .find((t: any) => t.name === 'Accept Offer' && t.status === 'completed');

      const preAuctionOutcome = preAuctionResultsTask?.fieldValues?.preAuctionOutcome;
      console.log('Pre-auction workflow check - Pre-auction outcome:', preAuctionOutcome);
      
      const shouldCreate = preAuctionOutcome === 'unsuccessful' || preAuctionOutcome === 'proceed_to_auction';
      console.log('Should create next workflow from pre-auction:', shouldCreate);
      return shouldCreate;
    }

    console.log('No matching workflow type found for next workflow creation');
    return false;
  } catch (error) {
    console.error('❌ Error checking if should create next workflow:', error);
    return false;
  }
};

// Create the next workflow based on the completed workflow
export const createNextWorkflow = async (completedWorkflow: any, property: any): Promise<any | null> => {
  try {
    console.log('🚀 Creating next workflow...');
    console.log('Completed workflow:', completedWorkflow.name, completedWorkflow.type);
    console.log('Property:', property._id, property.address?.street);
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No authentication token found');
      return null;
    }

    // Get agency slug from URL
    const urlParts = window.location.pathname.split('/');
    const agencySlug = urlParts[urlParts.indexOf('agencies') + 1];
    const propertyId = property._id;

    let workflowType = 'sale';
    let saleType = 'for sale';

    // Determine the next workflow type based on the completed workflow
    if (completedWorkflow.type === 'consultation') {
      const saleTypeTask = completedWorkflow.stages
        .flatMap((s: any) => s.tasks)
        .find((t: any) => (t.taskType === 'sale_type_determination' || t.name === 'Determine Sale Type') && t.status === 'completed');
      
      saleType = saleTypeTask?.fieldValues?.saleType || 'for sale';
      const hasSaleTypeTask = completedWorkflow.stages.some((s: any) =>
        s.tasks.some((t: any) => t.taskType === 'sale_type_determination' || t.name === 'Determine Sale Type')
      );
      if (!saleTypeTask?.fieldValues?.saleType && !hasSaleTypeTask) {
        saleType = 'rental';
      }
      
      if (saleType === 'auction' || saleType === 'pre-auction') {
        workflowType = 'auction';
      } else if (saleType === 'rental') {
        workflowType = 'rental';
      }
    } else if (completedWorkflow.type === 'auction') {
      if (completedWorkflow.name.includes('Pre-auction')) {
        workflowType = 'auction';
        saleType = 'auction';
      } else {
        workflowType = 'sale';
        saleType = 'for sale';
      }
    }

    console.log('Final workflow type:', workflowType);
    console.log('Final sale type:', saleType);
    console.log('Creating next workflow - Type:', workflowType, 'Sale type:', saleType);

    const requestBody = {
      workflowType,
      saleType
    };
    console.log('Request body:', requestBody);

    const response = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/create-workflow`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });

    console.log('Create workflow response status:', response.status);
    console.log('Create workflow response ok:', response.ok);
    
    if (response.ok) {
      const result = await response.json();
      console.log('✅ Next workflow created successfully:', result.workflow);
      return result.workflow;
    } else {
      const error = await response.json();
      console.error('❌ Error creating next workflow:', error);
      return null;
    }
  } catch (error) {
    console.error('❌ Error creating next workflow:', error);
    return null;
  }
};

export const createSpecificWorkflow = async (
  agencySlug: string,
  propertyId: string,
  workflowType: string,
  saleType: string
): Promise<any | null> => {
  try {
    console.log('🚀 Creating specific workflow...');
    console.log('Type:', workflowType, 'Sale type:', saleType);
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No authentication token found');
      return null;
    }

    const requestBody = {
      workflowType,
      saleType
    };

    const response = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/create-workflow`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });
    
    if (response.ok) {
      const result = await response.json();
      console.log('✅ Specific workflow created successfully:', result.workflow);
      return result.workflow;
    } else {
      const error = await response.json();
      console.error('❌ Error creating specific workflow:', error);
      return null;
    }
  } catch (error) {
    console.error('❌ Error creating specific workflow:', error);
    return null;
  }
};

// Skip a skippable stage
export const skipStage = async (
  agencySlug: string, 
  propertyId: string, 
  workflowId: string, 
  stageIndex: number
): Promise<any> => {
  const response = await api.post(
    `/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageIndex}/skip`
  );
  return response.data;
}; 

// Generate AI consultation report (PDF) and return storage info
export const generateConsultationReport = async (
  agencySlug: string,
  propertyId: string
): Promise<{ fileName: string; key: string; url: string }> => {
  const response = await api.post(
    `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/generate`
  );
  return response.data;
};